import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { learningModuleService, LearningModule as ServiceLearningModule } from '../../services/learningModuleService';
import { learningProgressService, ModuleProgress } from '../../services/learningProgressService';
import './LearningModule.css';

interface LearningModuleProps {
  user: User | null;
  onToggleSidebar: () => void;
}

interface Activity {
  activity_id: string;
  title: string;
  activity_type: 'lesson' | 'exercise' | 'assessment' | 'quiz' | 'game' | 'vocabulary';
  order_index: number;
  completed?: boolean;
  score?: number; // percentage
}

interface ModuleFileData {
  module_id: string;
  title: string;
  description: string;
  category: string;
  image_url?: string;
  activities: Activity[];
}

interface ModuleData extends ModuleFileData {
  // Service data
  module_id: string;
  url_alias: string;
  status: 'available' | 'in_progress' | 'completed' | 'draft';
  created_at?: string;
  updated_at?: string;
  // Display data
  level: string;
  progress: number;
  icon: string;
  totalActivities: number;
  completedActivities: number;
}

const LearningModule: React.FC<LearningModuleProps> = ({
  user,
  onToggleSidebar
}) => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const [moduleData, setModuleData] = useState<ModuleData | null>(null);
  const [progressData, setProgressData] = useState<ModuleProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'completed' | 'pending'>('all');

  useEffect(() => {
    if (moduleId && user) {
      loadModuleData(moduleId);
    }
  }, [moduleId, user]);

  const loadModuleData = async (id: string) => {
    setLoading(true);
    try {
      // First, try to get module info from the service using the ID as module_id
      let moduleInfo: ServiceLearningModule | null = null;
      
      // Try to get by ID first
      moduleInfo = await learningModuleService.getLearningModuleById(id);
      console.log(moduleInfo);
      
      if (!moduleInfo) {
        throw new Error(`Module ${id} not found in service`);
      }

      // Load the specific module's content JSON file using url_alias
      const moduleResponse = await fetch(`/modules/${moduleInfo.url_alias}/module.json`);
      if (!moduleResponse.ok) {
        throw new Error(`Failed to load module content for ${moduleInfo.url_alias}`);
      }
      const moduleFileData: ModuleFileData = await moduleResponse.json();

      // Fetch real progress data for this module
      const moduleProgress = await learningProgressService.getModuleProgress(moduleInfo.module_id);
      setProgressData(moduleProgress);

      // Create a map of activity progress by activity_id for quick lookup
      const activityProgressMap = new Map(
        moduleProgress.activity_progress.map(ap => [ap.activity_id || '', ap])
      );

      // Update activities with real progress data
      const activitiesWithProgress = moduleFileData.activities.map(activity => {
        const activityProgress = activityProgressMap.get(activity.activity_id);
        return {
          ...activity,
          completed: activityProgress?.status === 'completed',
          score: activityProgress?.score || undefined
        };
      });

      // Calculate real progress metrics
      const completedActivities = activitiesWithProgress.filter(a => a.completed).length;
      const totalActivities = activitiesWithProgress.length;
      const progress = totalActivities > 0 
        ? Math.round((completedActivities / totalActivities) * 100) 
        : 0;

      // Helper function to get icon based on category
      const getCategoryIcon = (category: string): string => {
        switch (category.toLowerCase()) {
          case 'language':
            return '🌍';
          case 'psychology':
            return '🧠';
          case 'science':
            return '🔬';
          case 'technology':
            return '💻';
          case 'mathematics':
            return '📐';
          default:
            return '📚';
        }
      };

      const combinedData: ModuleData = {
        ...moduleFileData,
        activities: activitiesWithProgress,
        // Service data
        module_id: moduleInfo.module_id,
        url_alias: moduleInfo.url_alias,
        status: moduleInfo.status,
        created_at: moduleInfo.created_at,
        updated_at: moduleInfo.updated_at,
        // Display data
        level: 'Intermediate', // Default level
        progress,
        icon: getCategoryIcon(moduleInfo.category),
        totalActivities,
        completedActivities
      };

      setModuleData(combinedData);
    } catch (error) {
      console.error('Error loading module data:', error);
      setModuleData(null);
    } finally {
      setLoading(false);
    }
  };


  const getActivityTypeIcon = (type: string) => {
    switch (type) {
      case 'lesson': return '📚';
      case 'exercise': return '📝';
      case 'assessment': return '📊';
      case 'quiz': return '❓';
      case 'game': return '🎮';
      case 'vocabulary': return '📖';
      default: return '📋';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'easy';
      case 'medium': return 'medium';
      case 'hard': return 'hard';
      default: return 'medium';
    }
  };

  const filteredActivities = moduleData?.activities.filter(activity => {
    if (selectedFilter === 'completed') return activity.completed;
    if (selectedFilter === 'pending') return !activity.completed;
    return true;
  }) || [];

  // Determine which activity is the next available one (first incomplete)
  const getNextAvailableActivityId = (): string | null => {
    if (!moduleData) return null;
    const sortedActivities = [...moduleData.activities].sort((a, b) => a.order_index - b.order_index);
    const nextActivity = sortedActivities.find(activity => !activity.completed);
    return nextActivity ? nextActivity.activity_id : null;
  };

  // Check if an activity is unlocked (either completed or is the next available)
  const isActivityUnlocked = (activityId: string): boolean => {
    if (!moduleData) return false;
    const activity = moduleData.activities.find(a => a.activity_id === activityId);
    if (!activity) return false;
    
    // If completed, it's unlocked
    if (activity.completed) return true;
    
    // If it's the next available activity, it's unlocked
    const nextAvailableId = getNextAvailableActivityId();
    return activity.activity_id === nextAvailableId;
  };

  const handleActivityClick = (activityId: string) => {
    // Check if activity is unlocked
    if (!isActivityUnlocked(activityId)) {
      console.log('Activity is locked:', activityId);
      return;
    }
    
    console.log('Opening activity:', activityId);
    // Navigate to lesson study page using the module_id from service data
    const moduleIdForRoute = moduleData?.module_id || moduleId;
    navigate(`/learn/${moduleIdForRoute}/${activityId}`);
  };

  const handleBackToModules = () => {
    navigate('/learn');
  };

  // Handle status updates when user completes activities
  const handleStatusUpdate = async (newStatus: 'available' | 'in_progress' | 'completed') => {
    if (!moduleData) return;
    
    try {
      await learningModuleService.updateModuleStatus(moduleData.module_id, newStatus);
      // Update local state
      setModuleData(prev => prev ? { ...prev, status: newStatus } : null);
    } catch (error) {
      console.error('Error updating module status:', error);
    }
  };

  if (loading) {
    return (
      <div className="learning-module-container">
        <div className="learning-module-loading">
          <div className="loading-spinner"></div>
          <p>Loading module...</p>
        </div>
      </div>
    );
  }

  if (!moduleData) {
    return (
      <div className="learning-module-container">
        <div className="learning-module-error">
          <h2>Module Not Found</h2>
          <p>The requested learning module could not be found.</p>
          <button className="back-btn" onClick={handleBackToModules}>
            ← Back to Learning Modules
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="learning-module-container">
      <div className="learning-module-header">
        <div className="learning-module-header-left">
          <button 
            className="learning-module-menu-btn"
            onClick={onToggleSidebar}
            title="Toggle sidebar"
          >
            ☰
          </button>
          <button 
            className="learning-module-back-btn"
            onClick={handleBackToModules}
            title="Back to modules"
          >
            ←
          </button>
          <div className="learning-module-title-section">
            <div className="learning-module-icon">{moduleData.icon}</div>
            <div style={{ flex: 1 }}>
              <h1 className="learning-module-title">{moduleData.title}</h1>
              <p className="learning-module-subtitle">
                {moduleData.level} • {moduleData.completedActivities}/{moduleData.totalActivities} completed ({moduleData.progress}%)
              </p>
              <div className="progress-bar-container" style={{ marginTop: '0.5rem' }}>
                <div 
                  className="progress-bar-fill" 
                  style={{ width: `${moduleData.progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="learning-module-content">
        <div className="exercises-grid">
          {filteredActivities.map((activity) => {
            const isUnlocked = isActivityUnlocked(activity.activity_id);
            return (
              <div 
                key={activity.activity_id}
                className={`exercise-card ${activity.completed ? 'completed' : 'pending'} ${!isUnlocked ? 'locked' : ''}`}
                onClick={() => handleActivityClick(activity.activity_id)}
              >
                <div className="exercise-header">
                  <div className="exercise-type-icon">
                    {getActivityTypeIcon(activity.activity_type)}
                  </div>
                  <div className="exercise-info">
                    <h3 className="exercise-title">{activity.title}</h3>
                    <div className="exercise-meta">
                      <span className={`difficulty-badge medium`}>
                        {activity.activity_type}
                      </span>
                      <span className="time-estimate">
                        #{activity.order_index}
                      </span>
                    </div>
                  </div>
                  {activity.completed && (
                    <div className="completion-status">
                      <span className="completion-checkmark">✓</span>
                      {activity.score && (
                        <span className="exercise-score">{activity.score}%</span>
                      )}
                    </div>
                  )}
                  {!isUnlocked && (
                    <div className="lock-status">
                      <span className="lock-icon">🔒</span>
                    </div>
                  )}
                </div>
                
                <div className="exercise-actions">
                  {activity.completed ? (
                    <button className="exercise-action-btn review">
                      📝 Review
                    </button>
                  ) : isUnlocked ? (
                    <button className="exercise-action-btn start">
                      ▶️ Start Activity
                    </button>
                  ) : (
                    <button className="exercise-action-btn locked" disabled>
                      🔒 Locked
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredActivities.length === 0 && (
          <div className="no-exercises">
            <p>No activities found for the selected filter.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningModule;
